import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useScrollLock } from 'common/hooks/useScrollLock';
import { Capacitor } from '@capacitor/core';
import { useAppSelector, useAppDispatch } from 'common/hooks/state';
import { RootState, persistor } from 'common/store/store';
import { linkPath } from 'common/constants';
import { clearCredentials } from 'features/login/authSlice';
import { baseApi } from 'common/store/baseApi';
import {
  LockClosedIcon,
  FingerPrintIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import {
  unlockSession,
  setWebAuthSetUp,
  setShowSetupPrompt,
  selectIsLocked,
  selectWebAuthSetUp,
  selectShowSetupPrompt,
} from 'common/store/lockSlice';
import { resetShepherd } from 'features/Shepherd/shepherdSlice';
import { useVerifyLockPasswordMutation } from 'features/login/userService';
import { useLogoutActionMutation } from 'features/MenuBar/MenuBarService';
import { useGetProfilePictureQuery } from 'features/Sharing/SharingService';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerCredential,
  authenticate as webAuthnAuthenticate,
  hasWebAuthnSetup,
  removeStoredCredential,
} from 'common/services/webAuthnService';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';

// Lock screen element ID for MutationObserver protection
const LOCK_SCREEN_ID = 'doqit-lock-screen';

// Convert technical WebAuthn errors to user-friendly messages. Pure — kept at
// module scope so it's always available regardless of component render order.
const getFriendlyAuthError = (errorMsg: string): string => {
  const lowerError = errorMsg.toLowerCase();
  if (lowerError.includes('timed out') || lowerError.includes('timeout')) {
    return 'Authentication timed out. Please try again.';
  }
  if (lowerError.includes('not allowed') || lowerError.includes('cancelled') || lowerError.includes('canceled')) {
    return 'Authentication cancelled. Use your password or try again.';
  }
  if (lowerError.includes('not supported')) {
    return 'Biometric authentication not supported on this device.';
  }
  return 'Authentication failed. Please try using your password.';
};

export const LockScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const isLocked = useAppSelector(selectIsLocked);
  const webAuthSetUp = useAppSelector(selectWebAuthSetUp);
  const showSetupPrompt = useAppSelector(selectShowSetupPrompt);

  // Web-only: Native apps use their own biometric lock (AppLifecycleHandler)
  const isNative = Capacitor.isNativePlatform();
  if (isNative) return null;

  const user = useAppSelector((state: RootState) => state.auth.user);
  const token = useAppSelector((state: RootState) => state.auth.token);
  const firstName = user?.user?.first_name || user?.first_name || '';
  const email = user?.user?.email || user?.email || '';
  const userId = user?.user?.id || user?.id;

  // Skip profile picture query when not authenticated (guest users)
  const { data: profilePictureData } = useGetProfilePictureQuery(undefined, { skip: !token });
  const profilePicture = profilePictureData?.data?.profile_picture || null;

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [webAuthnAvailable, setWebAuthnAvailable] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [verifyLockPassword] = useVerifyLockPasswordMutation();
  const [logoutAction] = useLogoutActionMutation();

  // Refs for focus management and DOM protection
  const lockScreenRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLInputElement | HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  // Check both Redux state AND localStorage for WebAuthn setup
  // (localStorage is the source of truth since that's where credential is stored)
  const isWebAuthnReady = webAuthSetUp || hasWebAuthnSetup();

  const isActive = isLocked || showSetupPrompt;

  // Check WebAuthn availability on mount
  useEffect(() => {
    const checkWebAuthn = async () => {
      const available = await isPlatformAuthenticatorAvailable();
      setWebAuthnAvailable(available && isWebAuthnSupported());
    };
    checkWebAuthn();
  }, []);

  // SECURITY: Lock body scroll when lock screen is active
  useScrollLock(isActive);

  // SECURITY: Keyboard trap - prevent Tab from escaping lock screen
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Block Escape key
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Trap Tab key within lock screen
    if (e.key === 'Tab') {
      const focusableElements = lockScreenRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first element, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        // Tab: if on last element, go to first
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  // SECURITY: MutationObserver to detect and prevent DOM removal
  useEffect(() => {
    if (!isActive) return undefined;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.id === LOCK_SCREEN_ID) {
            // Re-insert the lock screen if it was removed
            document.body.appendChild(node);
          }
        });
      });
    });

    // Observe the parent of lock screen for removals
    if (lockScreenRef.current?.parentElement) {
      observer.observe(lockScreenRef.current.parentElement, { childList: true });
    }

    return () => observer.disconnect();
  }, [isActive]);

  const handleWebAuthnUnlock = useCallback(async () => {
    setError('');
    setIsLoading(true);

    try {
      const result = await webAuthnAuthenticate();
      if (result.success) {
        dispatch(unlockSession());
      } else {
        setError(getFriendlyAuthError(result.error || 'Authentication failed'));
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Authentication failed';
      setError(getFriendlyAuthError(errorMsg));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  // Reset state when lock screen appears, then auto-trigger WebAuthn if set up
  useEffect(() => {
    if (isLocked) {
      setShowPasswordForm(false);
      setError('');
      setPassword('');
      // Auto-trigger WebAuthn when locked and set up
      if (isWebAuthnReady && webAuthnAvailable) {
        handleWebAuthnUnlock();
      }
    }
  }, [isLocked, isWebAuthnReady, webAuthnAvailable, handleWebAuthnUnlock]);

  // Don't show lock screen for unauthenticated (guest) users
  if (!token) return null;
  // Show if locked OR if showing the setup prompt (which happens after password unlock)
  if (!isLocked && !showSetupPrompt) return null;

  const handlePasswordUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await verifyLockPassword({ password }).unwrap();
      setPassword('');

      // Check both Redux state AND localStorage for existing WebAuthn setup
      const hasExistingSetup = webAuthSetUp || hasWebAuthnSetup();

      // Show WebAuthn setup prompt only if NOT already set up and biometrics available
      // Don't unlock yet - let them set up or skip first
      if (!hasExistingSetup && webAuthnAvailable) {
        dispatch(setShowSetupPrompt(true));
      } else {
        // Sync Redux state if localStorage has credential but Redux doesn't
        if (!webAuthSetUp && hasWebAuthnSetup()) {
          dispatch(setWebAuthSetUp({ isSetUp: true }));
        }
        dispatch(unlockSession());
      }
    } catch (err: any) {
      // Handle specific error messages from backend
      const errorMessage = err?.data?.message || 'Incorrect password. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupWebAuthn = async () => {
    setError('');
    setIsLoading(true);

    try {
      const result = await registerCredential(
        String(userId),
        email,
        firstName || email,
      );

      if (result.success) {
        dispatch(setWebAuthSetUp({ isSetUp: true, credentialId: result.credentialId }));
        dispatch(setShowSetupPrompt(false));
        dispatch(unlockSession());
      } else {
        setError(result.error || 'Setup failed');
      }
    } catch (err) {
      setError('Failed to set up fingerprint. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipSetup = () => {
    dispatch(setShowSetupPrompt(false));
    dispatch(unlockSession());
  };

  const handleSignOut = async () => {
    // Shared-device hygiene: a WebAuthn credential registered by this
    // browser/OS profile must not silently carry over to whoever logs in
    // next on the same device — clear both the localStorage credential and
    // the persisted lock-slice flags that mark it "set up".
    removeStoredCredential();
    dispatch(setWebAuthSetUp({ isSetUp: false }));
    // Explicit sign-out: a second account logging in on this browser must
    // not inherit this account's completed-tour flag.
    dispatch(resetShepherd());
    // Deliberately NOT calling clearSpotlight()/clearWidgetSnapshot() here
    // unconditionally the way MenuBar's onLogoutHandler does: this component
    // returns null on native platforms (see the isNative check above -
    // native apps use AppLifecycleHandler's own biometric lock instead), and
    // both of those helpers are no-ops off iOS. They would be permanently
    // dead code on this screen; clearCredentials below still purges them on
    // the (currently impossible) chance this ever runs on iOS.
    try {
      // Any resolved response is treated as a successful sign-out here,
      // same as MenuBar's onLogoutHandler and UserDropdownMenu's
      // handleLogout - not gated on the response body's own `status`
      // field. The alternative is a user who pressed "Sign out" staying
      // signed in locally because a typed boolean on an HTTP 200 said so,
      // which is worse than the reverse. All three sign-out paths must
      // agree on this so none of them re-diverges.
      await logoutAction().unwrap();
      // Only after the API call resolves: it needs a still-valid session
      // token, so clearing credentials any earlier would revoke the
      // client's own auth before it could ask the server to revoke it.
      // clearCredentials wipes the auth slice (and, via its reducer,
      // Sentry, pending-path, Spotlight, the widget snapshot and the native
      // shared preferences); resetApiState drops every RTK Query cache
      // entry so no other account's document data lingers in memory for
      // whoever signs in next on this device.
      trackEvent(MixpanelEventName.loggedOut, { source: 'lock_screen' });
      dispatch(clearCredentials());
      dispatch(baseApi.util.resetApiState());
      // This screen renders as a full-page overlay outside Router context
      // (hence window.location below, not useNavigate), which means the
      // impending navigation tears down the JS realm rather than just
      // re-rendering - redux-persist's write of the cleared credentials is
      // not otherwise guaranteed to land in localStorage before that
      // happens. Flush explicitly so it always does.
      //
      // The flush itself must never block what follows: if it rejects
      // (storage quota, private-browsing restrictions) the redirect below
      // still has to happen. clearCredentials has already updated the live
      // Redux state above, so a failed flush only risks the persisted copy
      // being stale until the next successful write, not this session
      // being wrong - but unlockSession() and the redirect are downstream
      // of this await, and without the catch a rejection here would skip
      // both, leaving the user stuck behind SecureContentWrapper's blurred,
      // pointer-events-none overlay (app.tsx) with no lock-screen UI left
      // to unlock it (this component itself unmounts once clearCredentials
      // nulls the token - see the `if (!token) return null` guard above) -
      // functionally indistinguishable from the bug this fix closes.
      await persistor.flush().catch(() => undefined);
      dispatch(unlockSession());
      // replace(), not a plain href assignment: this screen renders on top
      // of whatever page was open when the lock engaged, and a plain
      // assignment still pushes a new history entry on top of that page -
      // Android back from /login would return to it. replace() overwrites
      // that entry instead, the same protection MenuBar's onLogoutHandler
      // gets from navigate(..., { replace: true }).
      window.location.replace(linkPath.login);
    } catch (err) {
      // This screen already commits to telling the user they are signed
      // out on any failure here (see the comment below) - leaving the
      // token in place would silently contradict that, so it is cleared
      // the same way as the success path above.
      dispatch(clearCredentials());
      dispatch(baseApi.util.resetApiState());
      // Same non-blocking flush as the success path above, for the same
      // reason - must never prevent unlockSession()/the redirect below.
      await persistor.flush().catch(() => undefined);
      // Navigate anyway on error
      dispatch(unlockSession());
      window.location.replace(linkPath.login);
    }
  };

  // WebAuthn Setup Prompt (shown after password unlock)
  if (showSetupPrompt) {
    return (
      <div
        id={LOCK_SCREEN_ID}
        ref={lockScreenRef}
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          background: 'linear-gradient(180deg, #E6E1EB 0%, #CDC4D6 100%)',
        }}
        onKeyDown={handleKeyDown}
      >
        <div className="w-full max-w-sm px-4 flex flex-col items-center text-center">
          <div className="w-20 h-20 mb-6 rounded-full bg-white flex items-center justify-center shadow-lg">
            <FingerPrintIcon className="w-10 h-10 text-doqit-button-primary" />
          </div>

          <h2 className="text-2xl font-bold text-doqit-purple mb-2">
            Enable Fingerprint Unlock?
          </h2>
          <p className="text-doqit-purple-light mb-8">
            Unlock faster next time using your fingerprint or Face ID.
          </p>

          {error && (
            <p className="text-red-500 text-sm mb-4">{error}</p>
          )}

          <button
            type="button"
            onClick={handleSetupWebAuthn}
            disabled={isLoading}
            className="w-full py-4 bg-doqit-button-primary text-white font-semibold rounded-2xl
              hover:opacity-90 transition-opacity disabled:opacity-50 mb-3"
          >
            {isLoading ? 'Setting up...' : 'Enable Fingerprint'}
          </button>

          <button
            type="button"
            onClick={handleSkipSetup}
            className="w-full py-3 text-doqit-purple font-medium hover:text-doqit-button-primary transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  // Lock Screen
  return (
    <div
      id={LOCK_SCREEN_ID}
      ref={lockScreenRef}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'linear-gradient(180deg, #E6E1EB 0%, #CDC4D6 100%)',
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="w-full max-w-sm px-4 flex flex-col items-center text-center">
        {/* Lock Icon */}
        <div className="w-16 h-16 mb-4 rounded-full bg-white flex items-center justify-center shadow-lg">
          <LockClosedIcon className="w-8 h-8 text-doqit-button-primary" />
        </div>

        {/* User Avatar */}
        <div className="mb-4">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt={firstName}
              className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-doqit-button-primary flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {firstName.charAt(0).toUpperCase() || email.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <h2 className="text-xl font-bold text-doqit-purple mb-1">
          Session Locked
        </h2>
        <p className="text-doqit-purple-light mb-6">
          {firstName || email}
        </p>

        {error && (
          <p className="text-red-500 text-sm mb-4">{error}</p>
        )}

        {/* WebAuthn Button (if set up and not showing password form) */}
        {isWebAuthnReady && webAuthnAvailable && !showPasswordForm && (
          <div className="w-full mb-4">
            <button
              type="button"
              onClick={handleWebAuthnUnlock}
              disabled={isLoading}
              className="w-full py-4 bg-doqit-button-primary text-white font-semibold rounded-2xl
                hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <FingerPrintIcon className="w-6 h-6" />
              {isLoading ? 'Unlocking...' : 'Unlock with Fingerprint'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowPasswordForm(true);
                setError('');
              }}
              className="mt-3 text-doqit-purple text-sm hover:text-doqit-button-primary transition-colors"
            >
              Use password instead
            </button>
          </div>
        )}

        {/* Password Form (shown by default if no WebAuthn, or when user requests it) */}
        {(!isWebAuthnReady || !webAuthnAvailable || showPasswordForm) && (
          <form onSubmit={handlePasswordUnlock} className="w-full mb-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-5 py-4 bg-white border-0 rounded-2xl shadow-sm
                text-doqit-purple placeholder-doqit-purple-light focus:outline-none focus:ring-2
                focus:ring-doqit-button-primary transition-all mb-3"
            />
            <button
              type="submit"
              disabled={isLoading || !password}
              className="w-full py-4 bg-doqit-button-primary text-white font-semibold rounded-2xl
                hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isLoading ? 'Unlocking...' : 'Unlock'}
            </button>
            {/* Show option to go back to fingerprint if available */}
            {isWebAuthnReady && webAuthnAvailable && showPasswordForm && (
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setError('');
                  setPassword('');
                }}
                className="w-full mt-3 text-doqit-purple text-sm hover:text-doqit-button-primary transition-colors"
              >
                Use fingerprint instead
              </button>
            )}
          </form>
        )}

        {/* Sign Out Link */}
        <button
          type="button"
          onClick={handleSignOut}
          className="flex items-center justify-center gap-2 text-doqit-purple hover:text-red-500 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5" />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
};

export default LockScreen;
